// eslint-disable-next-line no-restricted-imports
import {EmojiAutocomplete} from '@github-ui/emoji-autocomplete'
import {testIdProps} from '@github-ui/test-id-props'
import {useSyncedState} from '@github-ui/use-synced-state'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {
  ActionList,
  type ActionListItemProps,
  ActionMenu,
  type DialogProps,
  FormControl,
  IconButton,
  TextInput,
} from '@primer/react'
import {subYears} from 'date-fns'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {MemexColumnDataType} from '../api/columns/contracts/memex-column'
import type {CustomColumnKind} from '../api/columns/contracts/storage'
import {SettingsFieldOptionHeaderUI, SettingsFieldOptionSave} from '../api/stats/contracts'
import {
  type ColumnDropdownItem,
  createApiRequest,
  createFieldTypeItems,
  getDefaultColumnTitle,
} from '../helpers/add-field-helpers'
import {replaceShortCodesWithEmojis} from '../helpers/emojis'
import {defaultIterationDuration} from '../helpers/iterations'
import {filterEmptyOptions} from '../helpers/new-column'
import {not_typesafe_nonNullAssertion} from '../helpers/non-null-assertion'
import {usePostStats} from '../hooks/common/use-post-stats'
import {useViews} from '../hooks/use-views'
import type {ColumnModel} from '../models/column-model'
import {useAllColumns} from '../state-providers/columns/use-all-columns'
import {useReservedColumnNames} from '../state-providers/columns/use-reserved-column-names'
import {Resources} from '../strings'
import {validateColumnTitle} from '../validations/validate-column-title'
import styles from './add-project-field-dialog.module.css'
import {getColumnIcon} from './column-detail-helpers'
import {NewIterationOptions} from './fields/iteration/new-iteration-options'
import {SingleSelectForm} from './fields/single-select/single-select-form'
import {useAddField} from './table/hooks/use-add-field'
import {useColumnSettings} from './table/use-column-settings'

export type UseAddProjectFieldDialogProps = {
  isOpen: boolean
  setOpen: (open: boolean) => void
  onSave: (newCols: Array<ColumnModel>) => void
  limitedTypes?: Array<CustomColumnKind>
  onBack?: () => void
}

/**
 * Custom hook that encapsulates stateful logic for the Add Project Field dialog.
 *
 * This hook returns dialog props that can be spread over a generic Dialog component,
 * enabling us to share the same dialog wrapper between the Add Issue Fields and Create Project Field dialogs.
 * This is needed, as Primer's Dialog component requires specifying props like Header, Body, and Footer
 * as props on the dialog itself, rather than as child components.
 *
 * @returns DialogProps object that can be spread over a Dialog component
 */
export function useAddProjectFieldDialog({
  isOpen,
  setOpen,
  onSave,
  limitedTypes,
  onBack,
}: UseAddProjectFieldDialogProps): DialogProps {
  const {allColumns} = useAllColumns()
  const {reservedColumnNames} = useReservedColumnNames()
  const {currentView} = useViews()
  const {addField} = useAddField()
  const {postStats} = usePostStats()
  const textInputRef = useRef<HTMLInputElement>(null)
  const optionsBottomRef = useRef<HTMLDivElement>(null)

  const {options, addOption, updateOption, removeOption, onDrop, canRemoveOption} = useColumnSettings({
    ui: SettingsFieldOptionHeaderUI,
  })

  const numUserDefinedColumns = allColumns.filter(c => c.userDefined).length
  const defaultColumnTitle = getDefaultColumnTitle(numUserDefinedColumns)
  const allFieldTypes = useMemo(() => createFieldTypeItems(), [])
  const availableFieldTypes = useMemo(
    () => (limitedTypes?.length ? allFieldTypes.filter(item => limitedTypes.includes(item.type)) : allFieldTypes),
    [allFieldTypes, limitedTypes],
  )

  const [title, setTitle] = useSyncedState(defaultColumnTitle, {isPropUpdateDisabled: isOpen})
  const [selectedColumn, setSelectedColumn] = useState<CustomColumnKind>(
    () => not_typesafe_nonNullAssertion(availableFieldTypes[0]).type,
  )
  const [dropdownSelection, setDropdownSelection] = useState(() =>
    not_typesafe_nonNullAssertion(availableFieldTypes[0]),
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
  const minIterationStartDate = useMemo(() => subYears(new Date(), 1), [])
  const [iterationStartDate, setIterationStartDate] = useState(() => new Date())
  const [iterationDuration, setIterationDuration] = useState(defaultIterationDuration)
  const [isIterationOptionsValid, setIsIterationOptionsValid] = useState(true)
  const [iterationValidationMessage, setIterationValidationMessage] = useState<string | undefined>(undefined)

  const prevOptionsLength = useRef(options.length)

  useEffect(() => {
    if (options.length > prevOptionsLength.current && selectedColumn === MemexColumnDataType.SingleSelect) {
      optionsBottomRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      })
    }
    prevOptionsLength.current = options.length
  }, [options.length, selectedColumn])

  const {isValid: isTitleValid, message: validationMessage} = validateColumnTitle(
    {allColumns, reservedColumnNames},
    title,
  )

  const isSingleSelectValid = useCallback(() => {
    return selectedColumn !== MemexColumnDataType.SingleSelect || filterEmptyOptions(options).length > 0
  }, [selectedColumn, options])

  const isIterationValid = () => {
    return selectedColumn !== MemexColumnDataType.Iteration || isIterationOptionsValid
  }

  const isFormValid = isSingleSelectValid() && isTitleValid && isIterationValid()
  const showValidationErrors = hasAttemptedSubmit && !isSubmitting

  const handleIterationValidationChange = useCallback((isValid: boolean, message?: string) => {
    setIsIterationOptionsValid(isValid)
    setIterationValidationMessage(message)
  }, [])

  const iterationValidation = useMemo(
    () => ({
      showValidationErrors,
      validationStyle: 'inline' as const,
      onChange: handleIterationValidationChange,
      validationMessage: iterationValidationMessage,
    }),
    [showValidationErrors, handleIterationValidationChange, iterationValidationMessage],
  )

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTitle(replaceShortCodesWithEmojis(e.currentTarget.value))
    },
    [setTitle],
  )

  const handleFieldTypeChange = useCallback(
    (item?: ActionListItemProps) => {
      if (!item) return

      const dropDownItem = item as ColumnDropdownItem

      if (title === defaultColumnTitle && dropDownItem.type === MemexColumnDataType.Iteration) {
        const iterationColumns = allColumns.filter(c => c.userDefined && c.name.startsWith('Iteration')).length
        setTitle(Resources.newIterationField(iterationColumns > 0 ? ` ${iterationColumns + 1}` : ''))
      }

      setDropdownSelection(dropDownItem)
      setSelectedColumn(dropDownItem.type)
      setHasAttemptedSubmit(false)
    },
    [allColumns, defaultColumnTitle, setTitle, title],
  )

  const handleSubmit = useCallback(async () => {
    setHasAttemptedSubmit(true)

    if (isSubmitting || !isFormValid) return

    setIsSubmitting(true)
    try {
      const request = createApiRequest(title, selectedColumn, {
        options,
        iterationStartDate,
        iterationDuration,
      })
      await addField.perform({request, viewNumber: currentView?.number})

      if (addField.status.current.status === 'succeeded') {
        const createdColumn = addField.status.current.data

        onSave([createdColumn])

        postStats({
          name: SettingsFieldOptionSave,
          ui: SettingsFieldOptionHeaderUI,
          memexProjectColumnId: addField.status.current.data?.id,
        })
        setOpen(false)
      }
    } finally {
      setIsSubmitting(false)
    }
  }, [
    isSubmitting,
    isFormValid,
    title,
    selectedColumn,
    options,
    iterationStartDate,
    iterationDuration,
    addField,
    currentView?.number,
    onSave,
    postStats,
    setOpen,
  ])

  const handleCancel = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack()
    }
  }, [onBack])

  const dialogTitle = useMemo(
    () =>
      onBack ? (
        <div className={styles.titleWithBack}>
          <IconButton
            aria-label="Go back"
            icon={ArrowLeftIcon}
            variant="invisible"
            onClick={handleBack}
            className={styles.backButton}
            {...testIdProps('add-project-field-back-button')}
          />
          <span>Create project field</span>
        </div>
      ) : (
        'Create project field'
      ),
    [onBack, handleBack],
  )

  const SelectedIcon = getColumnIcon(selectedColumn)

  const footerButtons = useMemo(
    () => [
      {
        buttonType: 'default' as const,
        content: 'Cancel',
        onClick: handleCancel,
        ...testIdProps('add-column-button-cancel'),
      },
      {
        buttonType: 'primary' as const,
        content: 'Create',
        onClick: handleSubmit,
        loading: isSubmitting,
        ...testIdProps('add-column-button-create'),
      },
    ],
    [handleCancel, handleSubmit, isSubmitting],
  )

  const renderBody = useCallback(
    () => (
      <>
        <form onSubmit={e => e.preventDefault()} className={styles.dialogBody}>
          <FormControl required className={styles.formControl}>
            <FormControl.Label>Field name</FormControl.Label>
            <EmojiAutocomplete fullWidth>
              <TextInput
                onFocus={e => e.currentTarget.select()}
                ref={textInputRef}
                value={title}
                onChange={handleTitleChange}
                aria-invalid={!isTitleValid}
                validationStatus={!isTitleValid && showValidationErrors ? 'error' : undefined}
                {...testIdProps('add-column-name-input')}
              />
            </EmojiAutocomplete>
            {!isTitleValid && showValidationErrors && (
              <FormControl.Validation variant="error">{validationMessage}</FormControl.Validation>
            )}
          </FormControl>

          <FormControl className={styles.formControl}>
            <FormControl.Label>Field type</FormControl.Label>
            <ActionMenu>
              <ActionMenu.Button {...testIdProps('add-column-type')}>
                <span className={styles.icon}>
                  <SelectedIcon />
                </span>
                {dropdownSelection.text}
              </ActionMenu.Button>
              <ActionMenu.Overlay>
                <ActionList selectionVariant="single">
                  {availableFieldTypes.map(item => {
                    const {key, ...rest} = item
                    return (
                      <ActionList.Item
                        key={key}
                        selected={dropdownSelection === item}
                        onSelect={() => handleFieldTypeChange(item)}
                        {...rest}
                      >
                        <ActionList.LeadingVisual>
                          <item.leadingVisual />
                        </ActionList.LeadingVisual>
                        {item.text}
                      </ActionList.Item>
                    )
                  })}
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </FormControl>

          {selectedColumn === MemexColumnDataType.SingleSelect && (
            <div className={styles.formControl}>
              <fieldset>
                <legend className={styles.fieldsetLegend}>Options</legend>
                <SingleSelectForm
                  options={options}
                  canRemoveOption={canRemoveOption}
                  onAddOption={addOption}
                  onUpdateOption={updateOption}
                  onRemoveOption={removeOption}
                  onDrop={onDrop}
                  noDuplicatedOptions={false}
                  enableNameLengthValidation={false}
                  enableDescriptionLengthValidation={false}
                  disallowCommasInName={false}
                  showOptionsBorder
                />
                {!isSingleSelectValid() && showValidationErrors && (
                  <FormControl.Validation
                    variant="error"
                    className={styles.formValidation}
                    {...testIdProps('single-select-validation')}
                  >
                    At least one option is required for single select fields
                  </FormControl.Validation>
                )}
              </fieldset>
            </div>
          )}

          {selectedColumn === MemexColumnDataType.Iteration && (
            <div className={styles.formControl}>
              <fieldset>
                <NewIterationOptions
                  duration={iterationDuration}
                  onDurationChange={setIterationDuration}
                  startDate={iterationStartDate}
                  onStartDateChange={setIterationStartDate}
                  minStartDate={minIterationStartDate}
                  compactLayout
                  validation={iterationValidation}
                />
              </fieldset>
            </div>
          )}
        </form>
        <div ref={optionsBottomRef} style={{height: 1}} />
      </>
    ),
    [
      title,
      handleTitleChange,
      isTitleValid,
      showValidationErrors,
      validationMessage,
      SelectedIcon,
      dropdownSelection,
      availableFieldTypes,
      handleFieldTypeChange,
      selectedColumn,
      options,
      canRemoveOption,
      addOption,
      updateOption,
      removeOption,
      onDrop,
      isSingleSelectValid,
      iterationDuration,
      setIterationDuration,
      iterationStartDate,
      setIterationStartDate,
      minIterationStartDate,
      iterationValidation,
    ],
  )

  return {
    className: styles.dialog,
    title: dialogTitle,
    onClose: handleCancel,
    height: 'auto' as const,
    width: 'large' as const,
    initialFocusRef: textInputRef,
    ...testIdProps('add-column-menu'),
    footerButtons,
    renderBody,
  }
}
